import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text as ChakraText,
  VStack
} from "@chakra-ui/react"
import type { FormikErrors } from "formik"
import { Field, Form, Formik } from "formik"
import moment from "moment"
import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { array, boolean, number, object, string } from "yup"
import { IKReactSelect } from "../../../../ui-kit"
import { CmFieldTypeEnum, MAX_CM_TEXT_LENGTH } from "../../../constants"
import { CmFieldResponseObject } from "../../../interfaces"
import DefaultValueFormComponent from "./DefaultValueFormComponent"
import SelectOptionsField from "./SelectOptionsFields"
import ValidationFormFields from "./ValidationFormFields"

interface Props {
  isOpen: boolean
  formValues?: CmFieldResponseObject
  isEditing?: boolean
  createCustomMetadataField(formValues: CmFieldResponseObject): Promise<void>
  editCustomMetadataField(formValues: CmFieldResponseObject): Promise<void>
  onClose(): void
}

const formatDate = (inputDate: string): string => {
  return moment(inputDate).format("MMMM Do YYYY")
}

const FormModal = ({
  isOpen,
  formValues,
  isEditing,
  createCustomMetadataField,
  editCustomMetadataField,
  onClose,
}: Props) => {
  const { t } = useTranslation()

  const formInitialValues = useMemo<CmFieldResponseObject>(() => {
    if (formValues) {
      return formValues
    }

    return {
      id: "",
      name: "",
      label: "",
      schema: {
        type: CmFieldTypeEnum.Text,
      },
    }
  }, [formValues])

  const typeSelectOptions = useMemo(() => [
    {
      label: CmFieldTypeEnum.Text,
      value: CmFieldTypeEnum.Text,
    },
    {
      label: CmFieldTypeEnum.Textarea,
      value: CmFieldTypeEnum.Textarea,
    },
    {
      label: CmFieldTypeEnum.Number,
      value: CmFieldTypeEnum.Number,
    },
    {
      label: CmFieldTypeEnum.Boolean,
      value: CmFieldTypeEnum.Boolean,
    },
    {
      label: CmFieldTypeEnum.Date,
      value: CmFieldTypeEnum.Date,
    },
    {
      label: CmFieldTypeEnum.SingleSelect,
      value: CmFieldTypeEnum.SingleSelect,
    },
    {
      label: CmFieldTypeEnum.MultiSelect,
      value: CmFieldTypeEnum.MultiSelect,
    },
  ], [])

  const validationSchema = useMemo(() => object().shape({
    label: string().required(t("required", "Required")),
    name: string().required(t("required", "Required")),
    schema: object().shape({
      type: string().oneOf([
        CmFieldTypeEnum.Text,
        CmFieldTypeEnum.Textarea,
        CmFieldTypeEnum.Number,
        CmFieldTypeEnum.Boolean,
        CmFieldTypeEnum.Date,
        CmFieldTypeEnum.SingleSelect,
        CmFieldTypeEnum.MultiSelect,
      ]),
      selectOptions: array(),
      isValueRequired: boolean(),
      minLength: number(),
      maxLength: number(),
    }),
  }), [])

  const validateForm = useCallback((values: CmFieldResponseObject) => {
    const errors: FormikErrors<CmFieldResponseObject> = {}
    
    // Get basic fields' validation out of the way through Yup
    try {
      validationSchema.validateSync(values, { strict: true, abortEarly: false })
    } catch(err) {
      (err as any)?.inner?.forEach(errObject => {
        errors[errObject.path] = errObject.message
      })
    }

    /*
      Default value presence check for mandatory fields
    */
    if (
      values.schema.isValueRequired &&
      (
        typeof values.schema.defaultValue === "undefined" ||
        values.schema.defaultValue === "" ||
        (Array.isArray(values.schema.defaultValue) && values.schema.defaultValue.length === 0)
      )
    ) {
      if (!errors.schema) {
        errors.schema = {}
      }
      errors.schema.defaultValue = t("default_value_required_for_mandatory_fields", "Default value is required for mandatory fields")
    }

    /*
      Select options validation checks (max length and duplicates)
    */
    if ([CmFieldTypeEnum.SingleSelect, CmFieldTypeEnum.MultiSelect].includes(values.schema.type)) {
      const exceedsLength = values.schema.selectOptions?.some(value => (
       typeof value === "string" &&
       value.length > MAX_CM_TEXT_LENGTH
      )) ?? false

      let hasDuplicates = false
      if (Array.isArray(values.schema.selectOptions)) {
        hasDuplicates = new Set(values.schema.selectOptions).size !== values.schema.selectOptions.length
      }

      const hasNoValues = (
        !values.schema.selectOptions ||
        !Array.isArray(values.schema.selectOptions) ||
        !values.schema.selectOptions.length
      )

      if (exceedsLength || hasDuplicates || hasNoValues) {
        if (!errors.schema) {
          errors.schema = {}
        }

        if (exceedsLength) {
          errors.schema.selectOptions = t("select_options_value_max_length_error", { maxLength: MAX_CM_TEXT_LENGTH })
        }

        if (hasDuplicates) {
          errors.schema.selectOptions = t("select_options_duplicates error", "Must not have duplicate values")
        }

        if (hasNoValues) {
          errors.schema.selectOptions = t("create_at_least_one_select_option", "Create at least one select option")
        }
      }
    }

    /*
      START: Default value validation checks
    */
    if ([CmFieldTypeEnum.Text, CmFieldTypeEnum.Textarea].includes(values.schema.type)) {

      if (typeof values.schema.defaultValue === "string" && values.schema.defaultValue.length > 0 &&
        values.schema.defaultValue.trim() === "") {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.defaultValue = t("value_cannot_be_empty_string", "Must be non empty string with at least one non-space character.")
      }

      if (
        typeof values.schema.defaultValue === "string" &&
        values.schema.defaultValue !== ""
      ) {
        if (
          (values.schema.minLength || values.schema.minLength === 0) &&
          values.schema.defaultValue.length < values.schema.minLength
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("custom_metadata_value_min_length_error", { minLength: values.schema.minLength })
        }

        if (
          (values.schema.maxLength || values.schema.maxLength === 0) &&
          values.schema.defaultValue.length > values.schema.maxLength
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("custom_metadata_value_max_length_error", { maxLength: values.schema.maxLength })
        }
        
        if (values.schema.defaultValue.length > MAX_CM_TEXT_LENGTH) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("custom_metadata_value_max_length_error", { maxLength: MAX_CM_TEXT_LENGTH })
        }
      }

      if (
        (values.schema.minLength || values.schema.minLength === 0) &&
        (values.schema.maxLength || values.schema.maxLength === 0) &&
        values.schema.minLength > values.schema.maxLength
      ) {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.minLength = t("min_length_max_length_discrepancy_error", "Minimum length must be less than or equal to maximum length")
      }

      if (
        (values.schema?.minLength && values.schema?.minLength < 0)
      ) {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.minLength = t("min_length_negative", "Min length cannot be negative value")
      }

      if (
        (values.schema?.maxLength && values.schema?.maxLength < 0)
      ) {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.maxLength = t("max_length_negative", "Max length cannot be negative value")
      }
    }

    if (values.schema.type === CmFieldTypeEnum.Number) {
      if (values.schema.defaultValue || values.schema.defaultValue === 0) {
        if (
          (values.schema.minValue || values.schema.minValue === 0) &&
          values.schema.defaultValue < values.schema.minValue
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("default_value_min_value_error", { minValue: values.schema.minValue })
        }
        
        if (
          (values.schema.maxValue || values.schema.maxValue === 0) &&
          values.schema.defaultValue > values.schema.maxValue
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("default_value_max_value_error", { maxValue: values.schema.maxValue })
        }
      }

      if (
        (values.schema.minValue || values.schema.minValue === 0) &&
        (values.schema.maxValue || values.schema.maxValue === 0) &&
        values.schema.minValue > values.schema.maxValue
      ) {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.minValue = t("min_value_max_value_discrepancy_error", "Minimum value must be less than or equal to maximum value")
      }
    }

    if (values.schema.type === CmFieldTypeEnum.Date) {
      if (values.schema.defaultValue) {
        if (
          values.schema.minValue &&
          new Date(values.schema.defaultValue as string) < new Date(values.schema.minValue)
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("default_value_min_value_error", { minValue: formatDate(values.schema.minValue as string) })
        }
        
        if (
          values.schema.maxValue &&
          new Date(values.schema.defaultValue as string) > new Date(values.schema.maxValue)
        ) {
          if (!errors.schema) {
            errors.schema = {}
          }
          errors.schema.defaultValue = t("default_value_max_value_error", { maxValue: formatDate(values.schema.maxValue as string) })
        }
      }

      if (
        values.schema.minValue &&
        values.schema.maxValue &&
        new Date(values.schema.minValue) > new Date(values.schema.maxValue)
      ) {
        if (!errors.schema) {
          errors.schema = {}
        }
        errors.schema.minValue = t("min_date_max_date_discrepancy_error", "Minimum date must be earlier than or equal to maximum date")
      }
    }
    /*
      END: Default value validation checks
    */

    return errors
  }, [validationSchema])

  return (
    <Formik<CmFieldResponseObject>
    initialValues={formInitialValues}
    onSubmit={async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isEditing) {
          await editCustomMetadataField(values)
        } else {
          await createCustomMetadataField(values)
        }
        onClose()
      } catch(err) {
        setSubmitting(false)
        return
      }
    }}
    validate={validateForm}
    validateOnMount
  >
    {
      ({ values, isValid, isSubmitting, resetForm, handleSubmit, handleChange, setValues, dirty }) => (
        <Form>
          <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay />

            <ModalContent>
              <ModalHeader>
                {
                  isEditing ?
                  t('edit_custom_metadata', 'Edit Custom Metadata') :
                  t('add_custom_metadata', 'Add Custom Metadata')
                }
              </ModalHeader>

              <ModalCloseButton />

              <ModalBody>
                <VStack width="full" spacing="1em">
                <Alert status="info" minW="auto">
                  <AlertIcon
                    height="1em"
                    width="1em"
                    alignItems="flex-start"
                    marginTop="4px"
                  />
                  <AlertDescription fontSize="sm">
                    {t('reset_default_cm', 'Updating the below field will reset the custom metadata in default settings')}
                  </AlertDescription>
                </Alert>
                  <Field name="label">
                    {
                      ({ field, meta }) => (
                        <FormControl isRequired isInvalid={meta.error && meta.touched}>
                            <FormLabel htmlFor='label'>{t('field_label', 'Field label')}</FormLabel>
                            <Input {...field} />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )
                    }
                  </Field>

                  <Field name="name">
                    {
                      ({ field, meta }) => (
                        <FormControl isDisabled={isEditing} isRequired isInvalid={meta.error && meta.touched}>
                            <FormLabel htmlFor='name'>{t('field_name', 'Field name')}</FormLabel>
                            <Input {...field} />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )
                    }
                  </Field>

                  <Field name="schema.type">
                    {
                      ({ field, meta }) => (
                        <FormControl isDisabled={isEditing} isRequired isInvalid={meta.error && meta.touched}>
                          <FormLabel htmlFor="schema.type">{t('field_type', 'Field type')}</FormLabel>
                          {!isEditing && (
                            <Alert
                              status="info"
                              marginBottom="10px"
                              minW="auto"
                            >
                               <AlertIcon
                                height="1em"
                                width="1em"
                                alignItems="flex-start"
                                marginTop="4px"
                              />
                              <AlertDescription fontSize="sm">
                                {t(
                                  'field_type_cannot_be_changed_once_created',
                                  'Field type cannot be changed once created',
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                          <Box cursor={isEditing ? "not-allowed" : "initial"}>
                            <IKReactSelect
                              menuPlacement="auto"
                              variant="compact"
                              isDisabled={isEditing}
                              captureMenuScroll={false}
                              isSearchable={false}
                              options={typeSelectOptions}
                              value={{
                                value: field.value,
                                label: field.value,
                              }}
                              onChange={newValue => {
                                setValues({
                                  ...values,
                                  schema: {
                                    type: newValue.value,
                                    isValueRequired: false,
                                  },
                                })
                              }}
                            />
                          </Box>
                          <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )
                    }
                  </Field>

                  {
                    [CmFieldTypeEnum.SingleSelect, CmFieldTypeEnum.MultiSelect].includes(values.schema?.type) ? (
                      <Field name="schema.selectOptions">
                        {
                          ({ field, meta, form }) => (
                            <FormControl isRequired isInvalid={meta.error}>
                              <FormLabel htmlFor="schema.selectOptions">
                                {t('options_for_select_field', 'Options for select field')}
                              </FormLabel>
                              <SelectOptionsField
                                field={field}
                                form={form}
                                meta={meta}
                              />
                              <FormErrorMessage>{meta.error}</FormErrorMessage>
                            </FormControl>
                          )
                        }
                      </Field>
                    ) : null
                  }

                  <Divider />

                  <Field name="schema.isValueRequired">
                    {
                      ({ field, meta }) => (
                        <FormControl>
                          <Checkbox isChecked={field.value} onChange={
                            e => handleChange({
                              target: {
                                name: "schema.isValueRequired",
                                value: e.target.checked,
                              },
                            })
                          }>
                            {t('mandatory_field', 'Mandatory Field')}
                          </Checkbox>
                          <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )
                    }
                  </Field>

                  <Field name="schema.defaultValue">
                    {
                      ({ field, meta, form }) => (
                        <FormControl isInvalid={meta.error}>
                            <FormLabel htmlFor='schema.defaultValue'>{t('default_value', 'Default value')}</FormLabel>
                            <DefaultValueFormComponent
                              field={field}
                              form={form}
                              type={values.schema.type}
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )
                    }
                  </Field>

                  <ValidationFormFields type={values.schema.type} />
                </VStack>
              </ModalBody>

              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {resetForm(); onClose()}}
                  variant="outline"
                >
                  {"Cancel"}
                </Button>

                <Button
                  type="submit"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  loadingText="Submitting"
                  onClick={() => handleSubmit()}
                >
                  {"Submit"}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Form>
      )
    }
  </Formik>
  )
}

export default FormModal
